import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTestComposer } from '@/hooks/useTestComposer';
import { useRequiresPlan } from '@/hooks/useRequiresPlan';
import PlanRequiredNotice from '@/components/ui/PlanRequiredNotice';
import { endpointApiPath } from '@/utils/endpointUrl';
import type { Endpoint } from '@/types';
import type {
  SavedTestReference,
  TestScenario,
  TestScenarioCategory,
} from '@/types/endpointTests';

interface GenerateTestsFlowProps {
  endpoint: Endpoint;
  onClose: () => void;
}

const COUNT_OPTIONS = [1, 3, 5, 8];

const CATEGORY_STYLES: Record<TestScenarioCategory, { label: string; cls: string }> = {
  typical: { label: 'typical', cls: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  edge: { label: 'edge case', cls: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' },
  boundary: { label: 'boundary', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
};

function CategoryChip({ category }: { category: TestScenarioCategory }) {
  const style = CATEGORY_STYLES[category] ?? CATEGORY_STYLES.typical;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.cls}`}>
      {style.label}
    </span>
  );
}

/** Count of schema properties, for the spec chips. */
function propertyCount(schema: Record<string, unknown> | null): number {
  const properties = schema?.properties as Record<string, unknown> | undefined;
  return properties ? Object.keys(properties).length : 0;
}

/**
 * One scenario card: name, category, description, the request body, rationale,
 * and what the turn saved it as.
 *
 * There is no Save button any more (task 795). The collaboration persists every
 * scenario during the turn — the report's P9 — so this reports a test the User
 * already owns instead of asking them to keep it. Curation moved to the Tests
 * panel's own list and delete.
 */
function ScenarioCard({
  scenario,
  savedTest,
}: {
  scenario: TestScenario;
  savedTest?: SavedTestReference;
}) {
  const saved = Boolean(savedTest);

  return (
    <div
      className={`rounded-lg border overflow-hidden bg-white dark:bg-gray-800 ${
        saved ? 'border-green-300 dark:border-green-800' : 'border-gray-200 dark:border-gray-700'
      }`}
      data-testid="scenario-card"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
        <span className="text-sm font-semibold text-gray-900 dark:text-white">{scenario.name}</span>
        <CategoryChip category={scenario.category} />
      </div>
      <div className="px-4 py-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">{scenario.description}</p>
        <pre className="mt-2 p-3 bg-gray-50 dark:bg-gray-900 rounded text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
          {JSON.stringify(scenario.input, null, 2)}
        </pre>
        {scenario.rationale && (
          <p className="mt-2 text-xs italic text-gray-400 dark:text-gray-500">
            Rationale: {scenario.rationale}
          </p>
        )}
      </div>
      <div className="flex justify-end px-4 py-2 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-100 dark:border-gray-700">
        {saved ? (
          <span
            className="inline-flex items-center text-xs font-semibold text-green-700 dark:text-green-400"
            data-testid="scenario-saved"
          >
            <CheckIcon className="h-4 w-4 mr-1" />
            {savedTest?.action === 'updated' ? 'Updated in your scenarios' : 'Saved as scenario'}
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">Not saved</span>
        )}
      </div>
    </div>
  );
}

/**
 * Generation flow (tasks 365, 795): the AI Service's definition is attached
 * automatically; the User adds optional guidance and a scenario count.
 *
 * WHAT CHANGED IN 795. The generator used to return proposals that this surface
 * saved one at a time, which made durability the caller's problem — a dropped
 * response lost the whole set. The collaboration now saves every scenario
 * itself, during the turn (the design-principles report's P9), so this renders
 * tests that already exist and the Tests panel's list is where they are curated.
 * Manual test creation is untouched.
 *
 * Follow-ups continue the same generation collaboration (collaborationId
 * continuation, the UI Composer iterate pattern) and REFINE the saved set
 * rather than adding a second copy of it.
 */
export function GenerateTestsFlow({ endpoint, onClose }: GenerateTestsFlowProps) {
  const composer = useTestComposer();
  const queryClient = useQueryClient();
  // Pre-flight no-plan gate (task 467): scenario generation invokes the
  // platform AI service, which fails closed without a plan.
  const requiresPlan = useRequiresPlan();

  const [guidance, setGuidance] = useState('');
  const [count, setCount] = useState(1);
  const [instruction, setInstruction] = useState('');

  /**
   * A generation turn writes tests server-side, so the panel's list is stale the
   * moment one lands. Invalidating on the saved references (rather than inside
   * each handler) covers both the first turn and every refinement.
   */
  const savedCount = composer.savedTests.length;
  useEffect(() => {
    if (savedCount > 0) {
      void queryClient.invalidateQueries({ queryKey: ['endpoint-tests', endpoint.id] });
    }
  }, [savedCount, composer.savedTests, endpoint.id, queryClient]);

  /** The saved test for a scenario, matched the way the save step keys them. */
  const savedFor = (scenario: TestScenario): SavedTestReference | undefined =>
    composer.savedTests.find((test) => test.name === scenario.name);

  const handleGenerate = async () => {
    if (requiresPlan) return;
    await composer.generate(endpoint, guidance, count);
  };

  const handleIterate = async () => {
    const text = instruction.trim();
    if (!text || requiresPlan) return;
    setInstruction('');
    await composer.iterate(text);
  };

  return (
    <div className="space-y-4" data-testid="generate-tests-flow">
      {/* Input step: spec chips + guidance + count */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <SparklesIcon className="h-5 w-5 mr-2 text-gray-400" />
            Generate test scenarios
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close generation flow"
            className="inline-flex items-center text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-4 w-4 mr-1" />
            Close
          </button>
        </div>
        <div className="p-4">
          {/* The endpoint definition is attached automatically — shown, never typed. */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-xs text-gray-600 dark:text-gray-300">
              <span className="font-medium text-indigo-700 dark:text-indigo-300 font-mono">{endpoint.method}</span>
              <span className="font-mono">{endpointApiPath(endpoint)}</span>
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-xs text-gray-600 dark:text-gray-300">
              input schema · {propertyCount(endpoint.input)} properties
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-xs text-gray-600 dark:text-gray-300">
              output schema · {propertyCount(endpoint.output)} properties
            </span>
          </div>

          <div className="mb-3">
            <label htmlFor="generate-tests-guidance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Guidance <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="generate-tests-guidance"
              type="text"
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              placeholder="e.g. Focus on multilingual threads and unusual message volumes"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <label htmlFor="generate-tests-count" className="sr-only">
              Scenario count
            </label>
            <select
              id="generate-tests-count"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {COUNT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} scenarios
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={composer.isGenerating || requiresPlan}
              data-testid="generate-tests-submit"
              className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {composer.isGenerating ? 'Generating…' : 'Generate'}
            </button>
          </div>
          {requiresPlan && <PlanRequiredNotice className="mt-2" />}
        </div>
      </div>

      {/* Generation errors: friendly message + raw code line */}
      {composer.error && (
        <div
          className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300"
          title={composer.errorCode ?? undefined}
        >
          {composer.error}
          {composer.errorCode && (
            <span className="mt-1 block font-mono text-xs text-red-500/80 dark:text-red-400/70">
              {composer.errorCode}
            </span>
          )}
        </div>
      )}

      {/* Review step: proposal cards, each with its own Save */}
      {composer.scenarios.length > 0 && (
        <>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {composer.scenarios.length} scenario{composer.scenarios.length === 1 ? '' : 's'} generated
            {savedCount > 0 && ' and saved to this service\u2019s scenarios'}
          </p>
          {composer.saveError && (
            <p
              data-testid="generate-tests-save-error"
              className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-300"
            >
              These scenarios were not saved: {composer.saveError}
            </p>
          )}
          <div className="space-y-3">
            {composer.scenarios.map((scenario, index) => (
              <ScenarioCard
                key={`${composer.turns.length}-${index}`}
                scenario={scenario}
                savedTest={savedFor(scenario)}
              />
            ))}
          </div>
        </>
      )}

      {/* Follow-up: continue the same generation conversation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleIterate();
              }
            }}
            rows={1}
            placeholder={
              composer.collaborationId
                ? 'Ask for more — e.g. “add scenarios with mixed agent/customer ordering”'
                : 'Generate scenarios first, then refine them here'
            }
            disabled={!composer.collaborationId || composer.isGenerating}
            className="flex-1 resize-none rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60"
          />
          <button
            type="button"
            onClick={() => void handleIterate()}
            disabled={!composer.collaborationId || composer.isGenerating || !instruction.trim() || requiresPlan}
            aria-label="Send follow-up"
            className="inline-flex items-center justify-center rounded-md bg-indigo-600 p-2.5 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
          Follow-ups continue the same generation conversation, so the composer
          remembers what it already produced and refines the scenarios it saved
          rather than adding duplicates.
        </p>
      </div>
    </div>
  );
}

/**
 * The buttons that open this in-page flow, moved out of `TestsPanel` (task
 * 1185) when the panel stopped owning a generate entry point. Same markup and
 * test id as before. Rendered through `TestsPanel`'s `renderGenerate` slot by
 * the two surfaces that keep the flow: the Advanced endpoint hub's Test tab and
 * the service Testing tab with `innovate.fe.testing.sections` off. The service
 * Testing tab's sections layout uses `GenerateTestsButton` (the Assistant) instead.
 */
export function OpenGenerateTestsFlowButton({
  placement,
  onOpen,
}: {
  placement: 'empty' | 'header';
  onOpen: () => void;
}) {
  if (placement === 'header') {
    return (
      <button
        type="button"
        onClick={onOpen}
        data-testid="tests-generate-open"
        className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700"
      >
        <SparklesIcon className="h-4 w-4 mr-1.5" />
        Generate
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className="mt-4 inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
    >
      <SparklesIcon className="h-4 w-4 mr-2" />
      Generate scenarios
    </button>
  );
}
